import { Interval } from '@mews-commander/types';
import { TimeUnit } from '@mews/enums';
import {
    DateTimeLocalizationInfo,
    DateTimeService,
    DateTimeUtc,
    DateTimeUtils,
    DurationService,
    TimeService,
    TimeUnitUtils,
} from '@mews/framework';
import { StructuredDate } from '@optimus-web/core';
import { TimeUnitPeriod } from '@mews-commander/data-access';
import { NewReservationDateTimeBoundaries } from '../types';

/**
 * @param dateUtc - 2023-11-30T23:00.000Z - 22/03/2025
 * @param localizedTime - 10:00 AM
 * @param localizationInfo
 * @param timezone
 * @returns 2023-12-01T09:00:00.000Z
 */
export const dateAndTimeToUtc = (
    dateUtc: string,
    localizedTime: string,
    localizationInfo: DateTimeLocalizationInfo,
    timezone: string
) => {
    const dateUtcValue = DateTimeService.fromUtcString(dateUtc, timezone);
    if (!dateUtcValue.value) {
        return null;
    }
    const mewsDate = DateTimeService.toDate(dateUtcValue);
    const hours = TimeService.fromShortString(localizedTime, localizationInfo, timezone);
    return DateTimeService.toUtc(DateTimeService.fromDateAndTime(mewsDate, hours));
};

export const getMinimalDateTimeInterval = (interval: Interval, timeUnit: TimeUnit, timezone: string): Interval => {
    const period = TimeUnitUtils.timeUnitToDateTimeUnit(timeUnit);

    const startUtc = TimeUnitUtils.getTimeUnitStartUtc(interval.startUtc, timezone, period);
    let endUtc = TimeUnitUtils.getTimeUnitStartUtc(interval.endUtc, timezone, period);

    if (endUtc === startUtc) {
        endUtc = TimeUnitUtils.addTimeUnitToUtc(startUtc, timezone, timeUnit, 1);
    }

    return { startUtc, endUtc };
};

export const getDateTimeBoundaries = (
    arrivalDate: string,
    departureDate: string,
    arrivalHours: string,
    departureHours: string,
    nowUtc: DateTimeUtc,
    operationalEditableHistoryWindow: string,
    timezone: string,
    timeUnitPeriod: TimeUnitPeriod,
    localizationInfo: DateTimeLocalizationInfo,
    areDatesEqual: (date1: StructuredDate, date2: StructuredDate) => boolean
): NewReservationDateTimeBoundaries => {
    const addHours = (time: string, amount: number) =>
        TimeService.toShortString(
            TimeService.addHours(TimeService.fromShortString(time, localizationInfo, timezone), amount),
            localizationInfo
        );

    const historyWindowInSeconds = DurationService.asSeconds(DurationService.parse(operationalEditableHistoryWindow));
    const minDateTime = DateTimeService.addSeconds(DateTimeService.fromUtc(nowUtc, timezone), -historyWindowInSeconds);
    const minStructuredDate = DateTimeUtils.dateTimeToStructuredDate(minDateTime);

    const departure = DateTimeService.fromUtcString(departureDate, timezone);

    const maxDate = DateTimeUtils.dateTimeToStructuredDate(
        timeUnitPeriod === TimeUnitPeriod.Hour ? departure : DateTimeService.subtractDays(departure, 1)
    );
    const minDate = DateTimeUtils.dateTimeToStructuredDate(DateTimeService.fromUtcString(arrivalDate, timezone));

    const sameDate = areDatesEqual(minDate, maxDate);

    return {
        arrivalMinDate: minStructuredDate,
        arrivalMaxDate: maxDate,
        departureMinDate: minDate,
        arrivalMaxHours: sameDate ? addHours(departureHours, -1) : undefined,
        departureMinHours: sameDate ? addHours(arrivalHours, 1) : undefined,
    };
};
